import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Filter,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  ImagePlus,
} from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { deleteBlog } from '../../../services/Writer/blogService';
import { getCategories } from '../../../services/Writer/blogCategoriesService';
import {
  approveDoctorBlog,
  listDoctorBlogs,
  rejectDoctorBlog,
  type DoctorBlog,
  type DoctorBlogApproval,
} from '../../../services/Writer/doctorBlogAdminService';
import BlogSeoFields, {
  BlogSeoFormValues,
  BlogSeoSource,
  blogSeoFromBlog,
  emptyBlogSeoValues,
} from '../../../components/Writer/Blogs/BlogSeoFields';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

type Category = {
  categoryId: string;
  name: string;
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

const approvalBadgeClass = (approval?: DoctorBlogApproval | null) => {
  switch (approval) {
    case 'approved':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
    default:
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200';
  }
};

const isSeoPending = (blog?: BlogSeoSource | null) => {
  if (!blog) return true;
  return !(
    blog.metaTitle?.trim() &&
    blog.metaDescription?.trim() &&
    blog.slug?.trim() &&
    blog.focusKeyword?.trim() &&
    blog.secondaryKeywords?.trim() &&
    blog.recommendedSchema?.trim()
  );
};

const DoctorBlogsPage: React.FC = () => {
  const navigate = useNavigate();
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState<DoctorBlog[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [approvalFilter, setApprovalFilter] = useState<DoctorBlogApproval | ''>('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmDescription, setConfirmDescription] = useState('');
  const [onConfirm, setOnConfirm] = useState<null | (() => Promise<void>)>(null);

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveBlog, setApproveBlog] = useState<DoctorBlog | null>(null);
  const [approveFile, setApproveFile] = useState<File | null>(null);
  const [approvePreview, setApprovePreview] = useState<string | null>(null);
  const [approveSeo, setApproveSeo] = useState<BlogSeoFormValues>(emptyBlogSeoValues());
  const [approveShowSeo, setApproveShowSeo] = useState(false);
  const [approveCategoryWithBlog, setApproveCategoryWithBlog] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectBlog, setRejectBlog] = useState<DoctorBlog | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  const filteredBlogs = useMemo(() => {
    if (!selectedCategory) return blogs;
    return blogs.filter((b) => b.categoryId === selectedCategory);
  }, [blogs, selectedCategory]);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.categoryId, c.name));
    return map;
  }, [categories]);

  const getBlogCategoryName = (blog: DoctorBlog) =>
    categoryNameById.get(blog.categoryId) || blog.category?.name || 'Unknown';

  const getDoctorName = (blog: DoctorBlog) =>
    blog.author?.name || blog.doctor?.doctorName || 'Doctor';

  const getDoctorDegree = (blog: DoctorBlog) =>
    blog.author?.qualificationDisplay || '';

  const loadBlogs = async () => {
    setLoading(true);
    try {
      const [cats, result] = await Promise.all([
        getCategories(),
        listDoctorBlogs({
          limit: 100,
          adminApproval: approvalFilter || undefined,
        }),
      ]);
      setCategories(cats);
      setBlogs(result.blogs);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Failed to load doctor blogs'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalFilter]);

  const openConfirm = (opts: {
    title: string;
    description: string;
    action: () => Promise<void>;
  }) => {
    setConfirmTitle(opts.title);
    setConfirmDescription(opts.description);
    setOnConfirm(() => opts.action);
    setConfirmOpen(true);
  };

  const onDelete = (blog: DoctorBlog) => {
    openConfirm({
      title: 'Delete blog?',
      description: `“${blog.title}” will be permanently removed. This action cannot be undone.`,
      action: async () => {
        await deleteBlog(blog.blogId);
        setBlogs((prev) => prev.filter((b) => b.blogId !== blog.blogId));
        toast.success('Blog deleted');
      },
    });
  };

  const openApprove = (blog: DoctorBlog) => {
    setApproveBlog(blog);
    setApproveFile(null);
    setApprovePreview(blog.featuredImage || null);
    setApproveSeo(blogSeoFromBlog(blog));
    setApproveShowSeo(isSeoPending(blog));
    setApproveCategoryWithBlog(false);
    setApproveOpen(true);
  };

  const openReject = (blog: DoctorBlog) => {
    setRejectBlog(blog);
    setRejectReason('');
    setRejectOpen(true);
  };

  const onApproveSubmit = async () => {
    if (!approveBlog) return;
    const hasThumb = Boolean(approveFile || approveBlog.featuredImage || approveBlog.hasThumbnail);
    if (!hasThumb) {
      toast.error('Please add a thumbnail before approving.');
      return;
    }

    if (approveShowSeo && isSeoPending(approveSeo)) {
      toast.error('Please fill the pending SEO fields before approving.');
      return;
    }
    if (
      approveBlog.category?.approvalStatus !== 'approved' &&
      !approveCategoryWithBlog
    ) {
      toast.error('Please approve the pending category before publishing this blog.');
      return;
    }

    setApproveLoading(true);
    try {
      await approveDoctorBlog(approveBlog.blogId, {
        thumbnailFile: approveFile,
        approveCategory: approveCategoryWithBlog,
        seo: {
          metaTitle: approveSeo.metaTitle.trim() || null,
          metaDescription: approveSeo.metaDescription.trim() || null,
          slug: approveSeo.slug.trim() || null,
          focusKeyword: approveSeo.focusKeyword.trim() || null,
          secondaryKeywords: approveSeo.secondaryKeywords.trim() || null,
          recommendedSchema: approveSeo.recommendedSchema.trim() || null,
        },
      });
      setBlogs((prev) =>
        prev.map((b) =>
          b.blogId === approveBlog.blogId
            ? {
                ...b,
                status: 'published',
                adminApproval: 'approved',
                hasThumbnail: true,
                featuredImage: approvePreview || b.featuredImage,
                rejectionReason: null,
                metaTitle: approveSeo.metaTitle.trim() || b.metaTitle,
                metaDescription: approveSeo.metaDescription.trim() || b.metaDescription,
                slug: approveSeo.slug.trim() || b.slug,
                focusKeyword: approveSeo.focusKeyword.trim() || b.focusKeyword,
                secondaryKeywords: approveSeo.secondaryKeywords.trim() || b.secondaryKeywords,
                recommendedSchema: approveSeo.recommendedSchema.trim() || b.recommendedSchema,
                category: b.category
                  ? {
                      ...b.category,
                      approvalStatus: approveCategoryWithBlog
                        ? 'approved'
                        : b.category.approvalStatus,
                      approvalReason: approveCategoryWithBlog
                        ? null
                        : b.category.approvalReason,
                    }
                  : b.category,
              }
            : b
        )
      );
      toast.success('Blog approved and published');
      setApproveOpen(false);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Failed to approve blog'));
    } finally {
      setApproveLoading(false);
    }
  };

  const onRejectSubmit = async () => {
    if (!rejectBlog) return;
    setRejectLoading(true);
    try {
      await rejectDoctorBlog(rejectBlog.blogId, rejectReason);
      setBlogs((prev) =>
        prev.map((b) =>
          b.blogId === rejectBlog.blogId
            ? {
                ...b,
                status: 'draft',
                adminApproval: 'rejected',
                rejectionReason: rejectReason.trim() || null,
              }
            : b
        )
      );
      toast.success('Blog rejected');
      setRejectOpen(false);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Failed to reject blog'));
    } finally {
      setRejectLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">Doctor Blogs</div>
          <div className="text-gray-600 dark:text-gray-400">
            Review blogs submitted by doctors. Add a thumbnail, then approve to publish.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        <div className="text-sm text-gray-700 dark:text-gray-300">Category:</div>
        <select
          value={selectedCategory ?? ''}
          onChange={(e) => setSelectedCategory(e.target.value ? e.target.value : null)}
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">All</option>
          {categories.map((c) => (
            <option key={c.categoryId} value={c.categoryId}>
              {c.name}
            </option>
          ))}
        </select>

        <div className="text-sm text-gray-700 dark:text-gray-300 ml-2">Approval:</div>
        <select
          value={approvalFilter}
          onChange={(e) => setApprovalFilter((e.target.value || '') as DoctorBlogApproval | '')}
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-600 dark:text-gray-400">Loading...</div>
      ) : filteredBlogs.length === 0 ? (
        <div className="py-16 text-center text-gray-600 dark:text-gray-400">No doctor blogs found.</div>
      ) : (
        <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/30 text-gray-700 dark:text-gray-200">
              <tr>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Doctor</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Approval</th>
                <th className="text-left p-3">Thumbnail</th>
                <th className="text-left p-3">Updated</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlogs.map((b) => (
                <tr key={b.blogId} className="border-t border-gray-200 dark:border-gray-700">
                  <td className="p-3 font-medium text-gray-900 dark:text-white">{b.title}</td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">
                    <div className="font-medium">{getDoctorName(b)}</div>
                    {getDoctorDegree(b) ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400">{getDoctorDegree(b)}</div>
                    ) : null}
                  </td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">{getBlogCategoryName(b)}</td>
                  <td className="p-3">
                    <div className="flex flex-col gap-1">
                      <span
                        className={`inline-flex w-fit px-2 py-1 rounded-full text-xs font-semibold capitalize ${approvalBadgeClass(
                          b.adminApproval
                        )}`}
                      >
                        {b.adminApproval || 'pending'}
                      </span>
                      {b.adminApproval === 'rejected' && b.rejectionReason ? (
                        <span
                          className="text-xs text-red-700 dark:text-red-300 max-w-[220px]"
                          title={b.rejectionReason}
                        >
                          Reason: {b.rejectionReason}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">
                    {b.hasThumbnail || b.featuredImage ? (
                      <span className="text-green-700 dark:text-green-300">Yes</span>
                    ) : (
                      <span className="text-amber-700 dark:text-amber-300">Missing</span>
                    )}
                  </td>
                  <td className="p-3 text-gray-700 dark:text-gray-300">{formatDate(b.updatedAt)}</td>
                  <td className="p-3">
                    <div className="flex justify-end">
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <button
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                            aria-label="Actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                          <DropdownMenu.Content
                            sideOffset={8}
                            align="end"
                            className="min-w-[200px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-1 text-sm"
                          >
                            <DropdownMenu.Item
                              onSelect={() => navigate(`/writer/blogs/view/${b.blogId}`, { state: b })}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 outline-none"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              onSelect={() => navigate(`/writer/blogs/edit/${b.blogId}`, { state: b })}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 outline-none"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </DropdownMenu.Item>

                            {b.adminApproval !== 'approved' ? (
                              <DropdownMenu.Item
                                onSelect={(e) => {
                                  e.preventDefault();
                                  openApprove(b);
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 outline-none"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                                Approve & Publish
                              </DropdownMenu.Item>
                            ) : null}

                            {b.adminApproval !== 'rejected' ? (
                              <DropdownMenu.Item
                                onSelect={(e) => {
                                  e.preventDefault();
                                  openReject(b);
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 outline-none"
                              >
                                <XCircle className="w-4 h-4" />
                                Reject
                              </DropdownMenu.Item>
                            ) : null}

                            <DropdownMenu.Separator className="h-px bg-gray-200 dark:bg-gray-700 my-1" />
                            <DropdownMenu.Item
                              onSelect={(e) => {
                                e.preventDefault();
                                onDelete(b);
                              }}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 outline-none"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={2500} />

      {/* Confirm delete */}
      <Dialog.Root open={confirmOpen} onOpenChange={(open) => (confirmLoading ? null : setConfirmOpen(open))}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              {confirmTitle}
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {confirmDescription}
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-3">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={confirmLoading}
                  className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-60"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={confirmLoading || !onConfirm}
                onClick={async () => {
                  if (!onConfirm) return;
                  setConfirmLoading(true);
                  try {
                    await onConfirm();
                    setConfirmOpen(false);
                  } catch (e: any) {
                    toast.error(getApiErrorMessage(e, 'Action failed'));
                  } finally {
                    setConfirmLoading(false);
                  }
                }}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition disabled:opacity-60"
              >
                {confirmLoading ? 'Please wait…' : 'Confirm'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Approve + thumbnail + SEO */}
      <Dialog.Root
        open={approveOpen}
        onOpenChange={(open) => {
          if (approveLoading) return;
          setApproveOpen(open);
          if (!open) {
            setApproveBlog(null);
            setApproveFile(null);
            setApprovePreview(null);
            setApproveSeo(emptyBlogSeoValues());
            setApproveShowSeo(false);
            setApproveCategoryWithBlog(false);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[94vw] max-w-2xl max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              Approve & publish blog
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {approveBlog
                ? `Approve “${approveBlog.title}” by ${getDoctorName(approveBlog)}. Thumbnail is required${
                    approveShowSeo ? ', and pending SEO fields should be filled' : ''
                  } before it goes live.`
                : 'A thumbnail is required before approving.'}
            </Dialog.Description>

            {approveBlog?.category?.approvalStatus !== 'approved' ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-900/20">
                <div className="font-medium text-amber-900 dark:text-amber-100">
                  Category approval required
                </div>
                <div className="mt-1 text-sm text-amber-800 dark:text-amber-200">
                  “{approveBlog?.category?.name || 'This category'}” is currently{' '}
                  {approveBlog?.category?.approvalStatus || 'pending'}.
                </div>
                {approveBlog?.category?.approvalReason ? (
                  <div className="mt-1 text-xs text-red-700 dark:text-red-300">
                    Previous reason: {approveBlog.category.approvalReason}
                  </div>
                ) : null}
                <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-gray-800 dark:text-gray-100">
                  <input
                    type="checkbox"
                    checked={approveCategoryWithBlog}
                    onChange={(event) => setApproveCategoryWithBlog(event.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  <span>
                    Approve this category together with the blog
                  </span>
                </label>
              </div>
            ) : null}

            <div className="mt-4">
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                Thumbnail *
              </div>
              {approvePreview ? (
                <img
                  src={approvePreview}
                  alt="Thumbnail preview"
                  className="w-full max-h-48 object-cover rounded-xl border border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div className="w-full h-40 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                  No thumbnail yet
                </div>
              )}

              <input
                ref={thumbnailInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setApproveFile(file);
                  if (file) {
                    setApprovePreview(URL.createObjectURL(file));
                  }
                }}
              />

              <button
                type="button"
                onClick={() => thumbnailInputRef.current?.click()}
                className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <ImagePlus className="w-4 h-4" />
                {approvePreview ? 'Change thumbnail' : 'Upload thumbnail'}
              </button>
            </div>

            {approveShowSeo ? (
              <div className="mt-5">
                <div className="mb-2 rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                  SEO fields are incomplete for this doctor blog. Fill them before approving.
                </div>
                <BlogSeoFields value={approveSeo} onChange={setApproveSeo} />
              </div>
            ) : (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setApproveShowSeo(true)}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Edit SEO Meta Package
                </button>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={approveLoading}
                  className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-60"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={approveLoading}
                onClick={onApproveSubmit}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition disabled:opacity-60"
              >
                {approveLoading ? 'Approving…' : 'Approve & Publish'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Reject */}
      <Dialog.Root
        open={rejectOpen}
        onOpenChange={(open) => {
          if (rejectLoading) return;
          setRejectOpen(open);
          if (!open) {
            setRejectBlog(null);
            setRejectReason('');
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              Reject blog
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {rejectBlog
                ? `Reject “${rejectBlog.title}”? Optionally leave a reason for the doctor.`
                : 'Optionally leave a rejection reason.'}
            </Dialog.Description>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Rejection reason (optional)"
              className="mt-4 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />

            <div className="mt-6 flex justify-end gap-3">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={rejectLoading}
                  className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-60"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="button"
                disabled={rejectLoading}
                onClick={onRejectSubmit}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-60"
              >
                {rejectLoading ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export default DoctorBlogsPage;
